import { useMemo } from 'react';
import { useEventInfo } from '@shared/utils/eventInfo';
import { useJoyPlaceIdBySourceIdQuery, useGetUserRatingFromHotelByIdQuery } from '@graphql/generated';
import { isValidUuId } from '@shared/utils/uuid';
import { addError } from '@shared/utils/logger';
import { useTransformRatingToCategory } from '../../../components/HotelsListV2/hooks/useTransformRatingToCategory';
import { HotelArgs } from '@apps/common/components/HotelRating/HotelRating';

interface UseHotelUserRatingResult {
  reviewCount?: number | null;
  validatedRating: number;
  ratingCategory: string;
  loading: boolean;
}

/**
 * Validates and clamps a rating value to the expected 0-5 scale.
 * Rounds to at most one decimal place.
 * Logs an error if the value is outside the valid range.
 */
const validateAndClampRating = (ratingValue: number | null | undefined, context: string): number => {
  if (ratingValue == null) {
    return 0;
  }

  const MIN_RATING = 0;
  const MAX_RATING = 10;

  const roundedValue = Math.round(ratingValue * 10) / 10;

  if (roundedValue < MIN_RATING || roundedValue > MAX_RATING) {
    const clampedValue = Math.max(MIN_RATING, Math.min(MAX_RATING, roundedValue));

    addError('Invalid hotel user rating', {
      originalValue: ratingValue,
      roundedValue,
      clampedValue,
      validRange: `${MIN_RATING}-${MAX_RATING}`,
      location: context
    });

    return clampedValue;
  }

  return roundedValue;
};

export const useHotelUserRating = ({ hotel, context = 'accommodation-user-rating', skip }: { hotel: HotelArgs; context?: string; skip?: boolean }): UseHotelUserRatingResult => {
  const { eventInfo, loading: loadingEvent } = useEventInfo();
  const eventId = eventInfo?.eventId;

  const shouldQueryJoyPlace = Boolean(eventId && hotel.zhId && !hotel.joyPlaceId);

  const { data: joyPlaceData, loading: loadingJoyPlace } = useJoyPlaceIdBySourceIdQuery({
    batchMode: 'fast',
    variables: {
      payload: {
        eventId: eventId!,
        sourceId: hotel.zhId!,
        sourceType: 'zh'
      }
    },
    skip: skip || !shouldQueryJoyPlace
  });

  const resolvedJoyPlaceId = hotel.joyPlaceId || joyPlaceData?.joyPlaceBySourceId?.id;

  // Validate accommodations place id before using it
  const isValidAccommodationPlaceId = (id: string | undefined | null): boolean => !!id && id !== hotel.zhId && isValidUuId(id);

  const accommodationPlaceId = isValidAccommodationPlaceId(hotel.itemId) ? hotel.itemId : isValidAccommodationPlaceId(hotel.id) ? hotel.id : null;

  // Determine which ID to use for the query
  const idObject = accommodationPlaceId ? { accommodationPlaceId } : resolvedJoyPlaceId ? { joyPlaceId: resolvedJoyPlaceId } : null;

  const { data: ratingData, loading: loadingRating } = useGetUserRatingFromHotelByIdQuery({
    batchMode: 'fast',
    variables: {
      idObject: idObject!
    },
    skip: skip || !idObject
  });

  const loading = loadingEvent || loadingJoyPlace || loadingRating;
  const userRating = ratingData?.accommodationCatalogPropertyById?.userRating;

  // Validate and clamp the rating value
  const validatedRating = useMemo(() => {
    return validateAndClampRating(userRating?.ratingValue, context);
  }, [userRating?.ratingValue, context]);

  // Determine rating category based on validated rating
  const ratingCategory = useTransformRatingToCategory(validatedRating);

  return {
    reviewCount: userRating?.ratingCount,
    validatedRating,
    ratingCategory,
    loading
  };
};
